import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Divider, Typography } from "@mui/material"
import { EditOutlined, SaveOutlined } from "@mui-symbols-material/w300"
import React, { type FC, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import type { Company } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import CompanyInfoForm from "@/screens/Companies/edit/components/info/components/form/CompanyInfoForm"
import useCompanyInfo from "@/screens/Companies/edit/components/info/hooks/useCompanyInfo"
import type { CompanyInfoFormValues } from "@/screens/Companies/edit/components/info/schema/CompanyInfoSchema"
import { CompanyInfoFormSchema } from "@/screens/Companies/edit/components/info/schema/CompanyInfoSchema"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

const CompanyInfoFormContainer: FC<{
  companyInfo: Company
}> = ({ companyInfo }) => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { updateCompany, fetchCompanyInfo } = useCompanyInfo({ companyId: companyInfo.companyId })

  const [isEditMode, setIsEditMode] = useState<boolean>(false)
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState<boolean>(false)

  const methods = useForm<CompanyInfoFormValues>({
    values: {
      industry: companyInfo?.industry?.filter((e) => e !== null) || [],
      equipment: companyInfo?.equipment?.filter((e) => e !== null) || [],
      market: companyInfo?.market?.filter((e) => e !== null) || [],
      generalInfo: companyInfo?.generalInfo || "",
      marketingPotential: companyInfo?.marketingPotential || "",
      technicalCapabilities: companyInfo?.technicalCapabilities || "",
      productionCapabilities: companyInfo?.productionCapabilities || "",
      reputation: companyInfo?.reputation || "",
    },
    mode: "onChange",
    resolver: zodResolver(CompanyInfoFormSchema),
  })

  const {
    handleSubmit,
    reset,
    formState: { isDirty, isValid },
  } = methods

  const isSubmitEnabled = isValid && isDirty

  const handleSubmitData = async (data: CompanyInfoFormValues) => {
    const { error, data: updatedCompany } = await updateCompany({
      input: {
        ...data,
        companyId: companyInfo.companyId,
      },
    })
    const hasError =
      error?.message ||
      (updatedCompany?.company.update.__typename === "CompanyUpdateFailure" &&
        Boolean(updatedCompany?.company.update.error))

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? "Failed to update company",
      })
      return
    }

    notify({
      type: NotificationType.Success,
      silent: false,
      content: "Company information successfully updated",
    })

    setIsEditMode(false)
    fetchCompanyInfo()
  }

  const handleCancel = () => {
    setIsCancelConfirmationOpen(false)
    setIsEditMode(false)
    reset()
  }

  const ActionButtons = isEditMode ? (
    <Box className='flex justify-between space-x-2'>
      <Button appearance='text' onClick={() => setIsCancelConfirmationOpen(true)}>
        Cancel
      </Button>
      <Button
        startIcon={<SaveOutlined />}
        appearance='outlined'
        disabled={!isSubmitEnabled}
        onClick={handleSubmit(handleSubmitData)}
      >
        Save
      </Button>
    </Box>
  ) : (
    <Button startIcon={<EditOutlined />} appearance='outlined' onClick={() => setIsEditMode(true)}>
      Edit Info
    </Button>
  )

  return (
    <Box>
      <Box className='my-6 flex justify-between'>
        <Box>
          <Typography variant='h5' className='text-gray-800'>
            Company Information
          </Typography>
        </Box>
        {ActionButtons}
      </Box>
      <Divider />
      <FormProvider {...methods}>
        <CompanyInfoForm isEditMode={isEditMode} />
      </FormProvider>

      {isCancelConfirmationOpen && (
        <CancelDialog
          title='Are you sure you want to cancel?'
          message='By cancelling all of the information inputed will be lost. Confirm by clicking on “Yes, Cancel” or “Go Back” to keep adding information.'
          onClose={() => setIsCancelConfirmationOpen(false)}
          onConfirm={handleCancel}
        />
      )}
    </Box>
  )
}

export default CompanyInfoFormContainer
